import React, { useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import './DataTable.css';

/**
 * Generic DataTable component for displaying tabular data with sorting, pagination, and actions
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Array of column definitions. Each column's `label` is
 *   rendered as plain uppercased text by default; supply an optional `headerRender()`
 *   function instead when the header cell needs to render something other than text
 *   (e.g. a select-all checkbox) — backward compatible, existing columns are untouched.
 * @param {Object} props.pagination - Pagination object with currentPage, totalPages, etc.
 * @param {Function} props.onSort - Callback for sorting (columnKey, direction)
 * @param {Function} props.onPageChange - Callback for page changes
 * @param {Function} props.onItemsPerPageChange - Callback for items per page changes
 * @param {Function} props.onActionClick - Callback for action button clicks (item, event)
 * @param {Function} props.onRowClick - Callback for row clicks (item, event)
 * @param {String} props.sortBy - Current sort column
 * @param {String} props.sortOrder - Current sort order ('asc' or 'desc')
 * @param {Boolean} props.isLoading - Loading state
 * @param {String} props.emptyMessage - Message to show when no data
 * @param {React.Node} props.createRow - Optional create row component
 * @param {String} props.className - Additional CSS classes
 */
const DataTable = ({
  data = [],
  columns = [],
  pagination = {},
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onActionClick,
  onRowClick,
  sortBy,
  sortOrder,
  isLoading = false,
  emptyMessage = 'No data available',
  createRow = null,
  className = '',
  storageKey = ''
}) => {

  // Column resize state: { [columnKey]: widthInPx }, initialized from localStorage
  const [columnWidths, setColumnWidths] = useState(() => {
    if (!storageKey) return {};
    try {
      const saved = localStorage.getItem(`datatable-colwidths-${storageKey}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const resizeRef = useRef(null);
  const isResizingRef = useRef(false);

  // Get sort direction indicator
  const getSortDirectionIndicator = (columnKey) => {
    if (sortBy !== columnKey) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Handle sort click (suppressed during resize)
  const handleSort = (columnKey) => {
    if (isResizingRef.current) return;
    if (onSort) {
      onSort(columnKey);
    }
  };

  // Calculate grid template columns based on column widths (with resize overrides)
  const getGridColumns = () => {
    return columns.map(col => {
      if (columnWidths[col.key]) {
        return `${columnWidths[col.key]}px`;
      }
      return col.width || 'auto';
    }).join(' ');
  };

  // Compute min-width so the table scrolls horizontally instead of compressing columns
  const getTableMinWidth = () => {
    const hasResizedColumn = Object.keys(columnWidths).length > 0;
    if (!hasResizedColumn) return undefined;
    let total = 0;
    for (const col of columns) {
      if (columnWidths[col.key]) {
        total += columnWidths[col.key];
      } else {
        // Parse fixed widths (e.g. '200px', '100px'); treat fr/auto as 120px minimum
        const w = col.width || 'auto';
        const pxMatch = w.match(/^(\d+)px$/);
        total += pxMatch ? parseInt(pxMatch[1], 10) : 120;
      }
    }
    return `${total}px`;
  };

  // Handle column resize via drag
  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    const startX = e.clientX;
    const headerCell = e.target.closest('.header-cell');
    const startWidth = headerCell ? headerCell.getBoundingClientRect().width : 200;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(100, startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist to localStorage
      if (storageKey) {
        setColumnWidths(prev => {
          try { localStorage.setItem(`datatable-colwidths-${storageKey}`, JSON.stringify(prev)); } catch {}
          return prev;
        });
      }
      // Clear resizing flag after click event fires
      setTimeout(() => { isResizingRef.current = false; }, 0);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [storageKey]);

  if (isLoading) {
    return (
      <div className={`data-table ${className}`}>
        <div className="data-table-loading">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (data.length === 0 && !createRow) {
    return (
      <div className={`data-table ${className}`}>
        <div className="data-table-empty">
          {typeof emptyMessage === 'string' ? <p>{emptyMessage}</p> : emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={`data-table ${className}`}>
      {/* Table Header */}
      <div
        className="data-table-header"
        style={{ gridTemplateColumns: getGridColumns(), minWidth: getTableMinWidth() }}
      >
        {columns.map((column) => (
          <div
            key={column.key}
            className={`data-table-cell header-cell ${column.sortable ? 'sortable-header' : ''} ${column.resizable ? 'resizable-header' : ''}`}
            onClick={column.sortable ? () => handleSort(column.key) : undefined}
          >
            {column.headerRender ? column.headerRender() : column.label.toUpperCase()}
            {column.sortable && getSortDirectionIndicator(column.key)}
            {column.resizable && (
              <div
                className="column-resize-handle"
                onMouseDown={(e) => handleResizeStart(e, column.key)}
              />
            )}
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div className="data-table-body">
        {/* Create Row */}
        {createRow && (
          <div
            className="data-table-row create-row"
            style={{ gridTemplateColumns: getGridColumns(), minWidth: getTableMinWidth() }}
          >
            {createRow}
          </div>
        )}

        {/* Data Rows */}
        {data.map((item, index) => (
          <div
            key={item.id || index}
            className={`data-table-row ${onRowClick ? 'clickable-row' : ''}`}
            style={{ gridTemplateColumns: getGridColumns(), minWidth: getTableMinWidth() }}
            onClick={onRowClick ? (e) => onRowClick(item, e) : undefined}
          >
            {columns.map((column) => (
              <div key={column.key} className={`data-table-cell ${column.className || ''}`}>
                {column.render ? column.render(item, index) : item[column.key]}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalItems > 0 && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} items
          </div>
          
          <div className="pagination-controls">
            <button
              onClick={() => onPageChange && onPageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="pagination-btn"
            >
              Previous
            </button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const startPage = Math.max(1, pagination.currentPage - 2);
              const pageNum = startPage + i;
              if (pageNum > pagination.totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange && onPageChange(pageNum)}
                  className={`pagination-btn ${pageNum === pagination.currentPage ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange && onPageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="pagination-btn"
            >
              Next
            </button>
          </div>

          <div className="items-per-page">
            <label>
              Items per page:
              <select 
                value={pagination.itemsPerPage} 
                onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value))}
                className="items-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to create action column with dropdown
 */
export const createActionColumn = (onActionClick, dropdownRefs, openDropdownId, toggleDropdown) => ({
  key: 'actions',
  label: '',
  width: '100px',
  className: 'actions-cell',
  render: (item) => (
    <div className="dropdown-container">
      <button
        ref={(el) => {
          if (el && dropdownRefs) {
            dropdownRefs.current[item.id] = el;
          }
        }}
        className="sidebar-action-button"
        onClick={(e) => toggleDropdown && toggleDropdown(item.id, e)}
        title={openDropdownId === item.id ? "" : "More actions"}
      >
        <div className="action-button-content">
          <FontAwesomeIcon icon={faEllipsisV} />
        </div>
      </button>
    </div>
  )
});

export default DataTable;
